import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import Chart from "chart.js"
import APIs from "redux/apis"
import { get, forEach, toNumber } from "lodash"
import dayjs from "dayjs"
import { convertDate } from "../logic/chartSpan"

interface props {
  canvasId?: string
}

const CHART_TYPE = ["line", "bar"]
const DATE_FORMMAT = "YYYY/MM/DD"

const LINE_CHART_SETTING = {
  borderColor: "rgba(255, 255, 132, 1)",
  borderWidth: 1,
}

const BAR_CHART_SETTING = {
  backgroundColor: "rgba(255, 99, 132, 0.2)",
  borderColor: "rgba(255, 99, 132, 1)",
  borderWidth: 1,
}

const IndicatorChart: React.FC<props> = props => {
  const { canvasId } = props

  const indicatorChartIDSelector = (state: any) =>
    state.indicatorChart[canvasId].ID
  const indicatorChartTargetDateSelector = (state: any) =>
    state.indicatorChart[canvasId].TargetDate
  const indicatorChartTypeSelector = (state: any) =>
    state.indicatorChart[canvasId].type
  const ID = useSelector(indicatorChartIDSelector)
  const TargetDate = useSelector(indicatorChartTargetDateSelector)
  const type = useSelector(indicatorChartTypeSelector)
  const [symbolData, setSymbolData] = useState({})
  const [chartObj, setChartObj] = useState<any>()

  const createDataSource = (data: object) => {
    const targets = get(data, "Chart", [])
    const labels: Array<string> = []
    const values: Array<number> = []
    forEach(targets, target => {
      const date = convertDate(get(target, "Date"))
      const _date = new Date(date[0], date[1], date[2])

      labels.push(String(dayjs(_date).format(DATE_FORMMAT)))
      values.push(get(target, "Value"))
    })
    return { labels, values }
  }

  useEffect(() => {
    drawChart()
  }, [])

  useEffect(() => {
    if (!ID && !TargetDate) return
    ;(async () => {
      const data = await APIs.Chart.IndicatorChart({ ID, TargetDate })
      setSymbolData(data)
    })()
  }, [ID, TargetDate])

  useEffect(() => {
    if (!chartObj) return
    const { labels, values } = createDataSource(symbolData)
    const _data = {
      labels: labels,
      datasets:
        type === 0
          ? [{ ...LINE_CHART_SETTING, data: values }]
          : [{ ...BAR_CHART_SETTING, data: values }],
    }
    chartObj.data = _data
    chartObj.update()
  }, [symbolData])

  useEffect(() => {
    drawChart()
  }, [type, symbolData])

  const drawChart = () => {
    // if (!chartObj) return
    chartObj && chartObj.destroy()
    const { labels, values } = createDataSource(symbolData)
    const _canvas = document.getElementById(canvasId) as HTMLCanvasElement
    const ctx = _canvas.getContext("2d")
    const _indicatorChart = new Chart(ctx, {
      type: CHART_TYPE[type],
      data: {
        labels: labels,
        datasets:
          type === 0
            ? [{ ...LINE_CHART_SETTING, data: values }]
            : [{ ...BAR_CHART_SETTING, data: values }],
      },
      options: {
        layout: {
          padding: {
            left: 10,
            right: 10,
            top: 10,
            bottom: 10,
          },
        },
        tooltips: {
          callbacks: {
            title: function(tooltipItem, data) {
              const tooltipIndex = tooltipItem[0]["index"]
              const labels = data["labels"]
              return labels[tooltipIndex]
            },
          },
        },
        scales: {
          xAxes: [
            {
              barThickness: 6, // number (pixels) or 'flex'
              maxBarThickness: 8, // number (pixels),
              type: "time",
              time: {
                min: dayjs(labels[0])
                  .subtract(1, "month")
                  .format("YYYY/MM/DD"),
                max: dayjs(labels[labels.length - 1])
                  .add(1, "month")
                  .format("YYYY/MM/DD"),
                unit: "year",
              },
              display: true,
              ticks: {
                maxTicksLimit: 5,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
              },
            },
          ],
        },
        legend: {
          display: false,
        },
      },
    })
    setChartObj(_indicatorChart)
  }

  return (
    <div>
      <canvas id={canvasId} width="200px" height="100px"></canvas>
    </div>
  )
}

export default IndicatorChart
